import React from "react";
import { CircleCheck } from "lucide-react";
import DashboardPanel from "./DashboardPanel";

/**
 * Today's activity check-in for a manager: how many assignees on active
 * projects have logged activity, and who hasn't yet.
 */
function LoggingStatusCard({ data, loading = false, error = false }) {
  let body;
  if (loading) {
    body = (
      <div className="space-y-4" aria-hidden="true">
        <div className="h-3 w-full animate-pulse rounded-full bg-gray-200 dark:bg-slate-700" />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-9 animate-pulse rounded bg-gray-100 dark:bg-slate-700/60" />
        ))}
      </div>
    );
  } else if (error) {
    body = <p className="text-sm text-app-error">{"Couldn't load activity logs."}</p>;
  } else if (!data.assigneeCount) {
    body = <p className="text-sm text-app-muted">No one is assigned to an active project.</p>;
  } else {
    const { assigneeCount, loggedTodayCount, notLoggedToday } = data;
    const pct = Math.round((loggedTodayCount / assigneeCount) * 100);
    body = (
      <div className="space-y-4">
        <div>
          <div className="mb-1.5 flex items-baseline justify-between text-sm">
            <span className="text-app-secondary">
              <span className="font-semibold text-app-primary">{loggedTodayCount}</span> of {assigneeCount} logged today
            </span>
            <span className="text-xs font-semibold tabular-nums text-app-muted">{pct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
            <div className="h-full rounded-full bg-blue-500 dark:bg-sky-400" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {notLoggedToday.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-app-success">
            <CircleCheck size={16} aria-hidden="true" /> Everyone has logged activity today.
          </p>
        ) : (
          <div>
            <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-app-muted">
              Not logged yet ({notLoggedToday.length})
            </h4>
            <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto dark:divide-slate-700">
              {notLoggedToday.map(({ email, projects }) => (
                <li key={email} className="py-2">
                  <p className="truncate text-sm font-medium text-app-primary" title={email}>{email}</p>
                  <p className="truncate text-xs text-app-muted" title={projects.map((p) => p.projectName).join(", ")}>
                    {projects[0]?.projectName}
                    {projects.length > 1 ? ` +${projects.length - 1} more` : ""}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <DashboardPanel title="Today's Check-in" description="Assignees on active projects who've logged activity today.">
      {body}
    </DashboardPanel>
  );
}

export default LoggingStatusCard;
